import { countryAlpha3Codes, getCountryInfoMappingByAlpha3Code } from '@shared/hooks/useGeoLocale/countries';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';

export type CountryEntry = Readonly<{
  countryCode: string;
  currencyCode: string;
}>;

/** Extracts flat country->currency pairs from getCashFundCurrencies currencyDescriptors. */
export const buildCountryEntries = (
  descriptors: ReadonlyArray<{
    currencyCode: string;
    countryDescriptors?: ReadonlyArray<{ countryCode: string }> | null;
  }>,
): ReadonlyArray<CountryEntry> => descriptors.flatMap(d => (d.countryDescriptors ?? []).map(cd => ({ countryCode: cd.countryCode, currencyCode: d.currencyCode })));

/** Default country code when browser locale doesn't match any supported country. */
export const DEFAULT_COUNTRY_CODE = 'USA';

/** Looks up the currency for a given country code from the entries list. */
export const getCurrencyForCountry = (entries: ReadonlyArray<CountryEntry>, countryCode: string): string =>
  entries.find(e => e.countryCode === countryCode)?.currencyCode ?? DEFAULT_CURRENCY;

export const getCountryLabels = (): Record<string, string> => {
  const labels: Record<string, string> = {};
  const countryMapping = getCountryInfoMappingByAlpha3Code();
  for (const alpha3 of countryAlpha3Codes) {
    const countryInfo = countryMapping.get(alpha3);
    if (countryInfo) {
      labels[alpha3] = countryInfo.name;
    }
  }
  return labels;
};

/** Matches a browser/geo currency to a Stripe-supported (country, currency) pair, or returns null when unsupported. */
export const resolveCashFundLocale = (entries: ReadonlyArray<CountryEntry>, browserCurrencyCode: string | undefined): CountryEntry | null => {
  if (!browserCurrencyCode) return null;
  return entries.find(e => e.currencyCode === browserCurrencyCode) ?? null;
};

/** Priority: registry currency -> shipping address -> browser locale -> fallback. */
export const resolveDefaultCountry = (
  entries: ReadonlyArray<CountryEntry>,
  registryCurrencyCode: string | undefined,
  shippingCountryCode: string | undefined,
  browserCurrencyCode: string | undefined,
): string => {
  if (registryCurrencyCode) {
    const match = entries.find(e => e.currencyCode === registryCurrencyCode);
    if (match) return match.countryCode;
  }

  if (shippingCountryCode) {
    const match = entries.find(e => e.countryCode === shippingCountryCode);
    if (match) return match.countryCode;
  }

  if (browserCurrencyCode) {
    const match = entries.find(e => e.currencyCode === browserCurrencyCode);
    if (match) return match.countryCode;
  }

  return DEFAULT_COUNTRY_CODE;
};
